import { formatInTimeZone } from 'date-fns-tz'

/**
 * Pure presentation helpers shared by the public events hub (U4) and the
 * restyled detail page (U5), so pill/CTA/date rendering always agree.
 *
 * Framework-free (no React/Next imports) so they're trivially unit-testable.
 * Status pills are derived ONLY from data the public payload actually carries
 * (capacityRemaining + waitlist config) — mockup-only labels that need data we
 * don't have ("Just added" needs a publish timestamp, "Selling well" needs
 * sales velocity) are intentionally dropped from v1.
 */

export type EventKind = 'ticketed' | 'signup' | 'application'

export type PillTone = 'pink' | 'lav' | 'green'

export interface StatusPill {
  label: string
  tone: PillTone
}

export interface StatusPillInput {
  capacityRemaining: number
  waitlist?: { enabled: boolean } | null
}

/**
 * Capacity-derived urgency pill. Thresholds are the single source of truth:
 *   <= 0  remaining → "Sold out" (or "Join waitlist" when a waitlist is open)
 *   1–10  remaining → "Almost full"
 *   11–25 remaining → "Going fast"
 *   > 25  remaining → null (no pill)
 */
export function statusPill(event: StatusPillInput): StatusPill | null {
  const remaining = event.capacityRemaining
  if (remaining <= 0) {
    return event.waitlist?.enabled ? { label: 'Join waitlist', tone: 'green' } : { label: 'Sold out', tone: 'pink' }
  }
  if (remaining <= 10) return { label: 'Almost full', tone: 'pink' }
  if (remaining <= 25) return { label: 'Going fast', tone: 'lav' }
  return null
}

// Which checkout-rail control set the detail page shows. Mirrors the existing
// detail-page routing (R9 — behavior unchanged): a sold-out event routes to
// waitlist/sold-out regardless of kind; otherwise the kind picks the control.
//   tickets  → ticket-tier steppers + "Get tickets" (RsvpFormClient)
//   waitlist → "Join waitlist" (WaitlistFormClient)
//   sold-out → disabled "Sold out" panel (no waitlist configured)
//   signup   → single "Sign up" CTA (RsvpFormClient, signup kind)
//   apply    → single "Apply" CTA (RsvpFormClient, application kind)
export type RailState = 'tickets' | 'waitlist' | 'sold-out' | 'signup' | 'apply'

export function detailRailState(event: {
  kind: EventKind
  capacityRemaining: number
  waitlist?: { enabled: boolean } | null
}): RailState {
  if (event.capacityRemaining <= 0) return event.waitlist?.enabled ? 'waitlist' : 'sold-out'
  if (event.kind === 'signup') return 'signup'
  if (event.kind === 'application') return 'apply'
  return 'tickets'
}

/** Format a price in cents as a compact string ("$12", "$12.50"). */
export function formatPriceCents(cents: number): string {
  const dollars = cents / 100
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`
}

export interface CtaInput {
  kind: EventKind
  // Detail page passes the full tier list so the CTA can be price-derived;
  // the hub list omits ticketTypes (not in the public payload), so a ticketed
  // event falls back to the plain action verb.
  ticketTypes?: Array<{ priceCents: number | null }>
}

/**
 * Kind-aware CTA label. `signup` → "Sign up", `application` → "Apply".
 * `ticketed` derives from price when tiers are known: any paid tier → "From
 * $X", all-free tiers → "Free", unknown (hub) → "Get tickets".
 */
export function ctaLabel(event: CtaInput): string {
  if (event.kind === 'signup') return 'Sign up'
  if (event.kind === 'application') return 'Apply'

  const tiers = event.ticketTypes
  if (!tiers || tiers.length === 0) return 'Get tickets'
  const paid = tiers.map(t => t.priceCents).filter((p): p is number => typeof p === 'number' && p > 0)
  if (paid.length === 0) return 'Free'
  return `From ${formatPriceCents(Math.min(...paid))}`
}

/**
 * Human date line for cards/detail, e.g. "Sat, Sep 13 · 10:00 AM". When a
 * duration is supplied the end time is appended ("… 10:00 AM – 11:00 AM").
 *
 * Rendered in the SCHOOL's timezone (`event.school.timezone`), NOT the visitor's
 * device zone. These pages are public: a grandparent reading a Miami school's
 * event page from Denver must see the Miami time, or they arrive two hours off.
 * Browser-local formatting also made the SSR pass (UTC on Vercel) disagree with
 * the hydrated client. Falls back to UTC when the school has no zone on file —
 * the same fallback the admin write path uses, so the two can never diverge.
 */
export function formatEventDate(
  startsAt: string | Date,
  durationMinutes?: number,
  schoolTimezone?: string | null
): string {
  const start = typeof startsAt === 'string' ? new Date(startsAt) : startsAt
  const tz = schoolTimezone && schoolTimezone.trim() ? schoolTimezone : 'UTC'
  const date = formatInTimeZone(start, tz, 'EEE, MMM d')
  const time = formatInTimeZone(start, tz, 'h:mm a')
  if (durationMinutes && durationMinutes > 0) {
    const end = new Date(start.getTime() + durationMinutes * 60_000)
    return `${date} · ${time} – ${formatInTimeZone(end, tz, 'h:mm a')}`
  }
  return `${date} · ${time}`
}
